/*
 * Copyright (c) 2025 NoorWays. All rights reserved.
 * Licensed under the terms specified in the LICENSE.txt file.
 */

import { loadStripe, type Stripe } from '@stripe/stripe-js'
import appConfig from '@/config/appConfig'
import { logger } from './logger'

/**
 * Stripe service for handling payment processing
 */
export class StripeService {
  private stripe: Stripe | null = null
  private initialized = false

  /**
   * Initialize Stripe with the publishable key
   */
  async initialize(): Promise<boolean> {
    if (this.initialized && this.stripe) {
      return true
    }

    if (!appConfig.stripePublishableKey) {
      return false
    }

    try {
      this.stripe = await loadStripe(appConfig.stripePublishableKey)
      this.initialized = true
      return this.stripe !== null
    } catch {
      return false
    }
  }

  /**
   * Redirect to Stripe Checkout using session ID
   */
  async redirectToCheckout(sessionId: string): Promise<{ success: boolean; error?: string }> {
    if (!await this.initialize()) {
      return { success: false, error: 'Stripe not initialized' }
    }

    if (!this.stripe) {
      return { success: false, error: 'Stripe instance not available' }
    }

    try {
      const { error } = await this.stripe.redirectToCheckout({ sessionId })
      
      if (error) {
        return { success: false, error: error.message }
      }

      return { success: true }
    } catch (error) {
      logger.error('Error redirecting to checkout:', error instanceof Error ? error : undefined, { sessionId }, 'stripeService')
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to redirect to checkout' 
      }
    }
  }

  /**
   * Get Stripe instance (for advanced usage)
   */
  async getStripe(): Promise<Stripe | null> {
    if (!await this.initialize()) {
      return null
    }
    return this.stripe
  }
}

/**
 * Singleton instance of StripeService
 */
export const stripeService = new StripeService()

export default stripeService
